import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import useAuthStore from './store/authStore';

// Pages
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Documents from './pages/Documents';
import DocumentDetail from './pages/DocumentDetail';
import Folders from './pages/Folders';
import Chatbot from './pages/Chatbot';
import Search from './pages/Search';
import AuditLog from './pages/AuditLog';
import Users from './pages/Users';
import AdminDashboard from './pages/AdminDashboard';
import SecurityEvents from './pages/SecurityEvents';
import SystemSettings from './pages/SystemSettings';
import MeToAction from './pages/MeToAction';
import SupplierRecon from './pages/SupplierRecon';
import SupplierReconSession from './pages/SupplierReconSession';
import Suppliers from './pages/Suppliers';
import PublicReconReport from './pages/PublicReconReport';
import BusinessIntelligence from './pages/BusinessIntelligence';
import BusinessIntelligenceSession from './pages/BusinessIntelligenceSession';

// Layout
import Layout from './components/Layout';

// Per-user module restriction — mirrors Layout.jsx's hasModuleAccess so a direct URL can't
// bypass what the sidebar already hides. Admins always pass regardless of flag values.
const hasModuleAccess = (profile, module) => {
  if (!module) return true;
  if (profile?.role === 'admin') return true;
  return profile?.[`access_${module}`] !== false;
};

// First module a restricted user can actually reach, used to redirect away from a blocked
// route instead of leaving them on a page they're not allowed to see.
const MODULE_FALLBACK_ORDER = [
  { module: 'dashboard', path: '/' },
  { module: 'business_intelligence', path: '/business-intelligence' },
  { module: 'supplier_recon', path: '/supplier-recon' },
  { module: 'documents', path: '/documents' },
  { module: 'folders', path: '/folders' },
  { module: 'ai_assistant', path: '/chatbot' },
  { module: 'search', path: '/search' },
  { module: 'me_to_action', path: '/me-to-action' },
];

const resolveFallbackPath = (profile) => {
  const allowed = MODULE_FALLBACK_ORDER.find(({ module }) => hasModuleAccess(profile, module));
  return allowed?.path || '/login';
};

// Protected Route Component
const ProtectedRoute = ({ children, module }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const profile = useAuthStore((state) => state.profile);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasModuleAccess(profile, module)) {
    return <Navigate to={resolveFallbackPath(profile)} replace />;
  }

  return <Layout>{children}</Layout>;
};

function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    checkAuth().finally(() => setAuthChecked(true));
  }, [checkAuth]);

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600">Initializing...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/r/:token" element={<PublicReconReport />} />
      <Route
        path="/"
        element={
          <ProtectedRoute module="dashboard">
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute module="documents">
            <Documents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents/:id"
        element={
          <ProtectedRoute module="documents">
            <DocumentDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/folders"
        element={
          <ProtectedRoute module="folders">
            <Folders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chatbot"
        element={
          <ProtectedRoute module="ai_assistant">
            <Chatbot />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute module="search">
            <Search />
          </ProtectedRoute>
        }
      />
      <Route
        path="/me-to-action"
        element={
          <ProtectedRoute module="me_to_action">
            <MeToAction />
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-recon"
        element={
          <ProtectedRoute module="supplier_recon">
            <SupplierRecon />
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-recon/:id"
        element={
          <ProtectedRoute module="supplier_recon">
            <SupplierReconSession />
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute module="supplier_recon">
            <Suppliers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-intelligence"
        element={
          <ProtectedRoute module="business_intelligence">
            <BusinessIntelligence />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-intelligence/:id"
        element={
          <ProtectedRoute module="business_intelligence">
            <BusinessIntelligenceSession />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute>
            <AuditLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/security"
        element={
          <ProtectedRoute>
            <SecurityEvents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute>
            <SystemSettings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
